import { Link } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { X, ArrowRight, ClipboardList } from "@/components/Icon";
import { useQuoteCart } from "@/contexts/QuoteCart";
import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

const QuoteDrawer = () => {
  const { items, isOpen, close, removeItem, updateSizeQuantity, clear, totalQuantity } = useQuoteCart();

  useEffect(() => {
    if (isOpen) {
      trackEvent("offerte_geopend", {
        aantal_producten: items.length,
        aantal_stuks: totalQuantity,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  return (
    <Sheet open={isOpen} onOpenChange={(o) => (o ? null : close())}>
      <SheetContent side="right" className="w-full sm:max-w-md flex flex-col p-0">
        <SheetHeader className="p-5 border-b">
          <SheetTitle className="flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-[#059594]" />
            Offerte-aanvraag
          </SheetTitle>
          <SheetDescription>
            {items.length === 0
              ? "Voeg producten toe om een vrijblijvende offerte aan te vragen."
              : `${items.length} ${items.length === 1 ? "product" : "producten"} klaar voor offerte.`}
          </SheetDescription>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto p-5 space-y-3">
          {items.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-10">
              Nog geen producten toegevoegd.
              <div className="mt-4">
                <Button asChild variant="outline" onClick={close}>
                  <Link to="/producten">Bekijk producten</Link>
                </Button>
              </div>
            </div>
          ) : (
            items.map((item) => {
              const itemTotal = Object.values(item.sizes).reduce((s, n) => s + n, 0);
              return (
                <div
                  key={`${item.ref}-${item.color ?? ""}`}
                  className="flex flex-col gap-3 p-3 rounded-lg border border-border bg-white"
                >
                  <div className="flex gap-3">
                    <div className="w-16 h-16 shrink-0 bg-white border border-border rounded-md overflow-hidden flex items-center justify-center">
                      {item.image ? (
                        <img src={item.image} alt={item.name} className="w-full h-full object-contain p-1" />
                      ) : (
                        <span className="text-[10px] text-muted-foreground">geen foto</span>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      {item.brand && (
                        <div className="text-[11px] text-muted-foreground truncate">{item.brand}</div>
                      )}
                      <div className="text-sm font-semibold text-[#0a2933] truncate">{item.name}</div>
                      <div className="text-[11px] text-muted-foreground">
                        {item.ref}
                        {item.color ? ` • ${item.color}` : ""}
                      </div>
                      <div className="text-[11px] text-primary font-semibold mt-1">
                        Totaal: {itemTotal} stuks
                      </div>
                    </div>
                    <button
                      onClick={() => removeItem(item.ref, item.color)}
                      aria-label="Verwijderen"
                      className="text-muted-foreground hover:text-destructive shrink-0 self-start"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {Object.entries(item.sizes).map(([size, qty]) => (
                      <div
                        key={size}
                        className="flex items-center gap-1 px-2 py-1 border border-border rounded bg-muted/30"
                      >
                        <span className="text-[11px] font-medium w-6">{size}</span>
                        <input
                          type="number"
                          min={0}
                          value={qty}
                          onChange={(e) =>
                            updateSizeQuantity(
                              item.ref,
                              item.color,
                              size,
                              parseInt(e.target.value || "0", 10),
                            )
                          }
                          className="w-full h-6 px-1 text-xs border border-border rounded text-center"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })
          )}
        </div>

        {items.length > 0 && (
          <div className="border-t p-5 space-y-3 bg-card">
            <Button asChild size="lg" className="w-full" onClick={close}>
              <Link to="/offerte">
                Gratis offerte aanvragen <ArrowRight className="ml-2 w-4 h-4" />
              </Link>
            </Button>
            <button
              onClick={clear}
              className="w-full text-xs text-muted-foreground hover:text-destructive"
            >
              Lijst leegmaken
            </button>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default QuoteDrawer;
